import { Article, ArticleMeta } from 'types/blog';
import { SITE_ORIGIN } from 'hooks/useSeo';
import { stripInline } from 'sections/blog/InlineText';
import { articlePath } from './index';

// ==============================|| 記事ページの構造化データ ||============================== //
//
// 記事ページにだけ出す JSON-LD を組み立てる。
// サイト共通のもの(Organization / WebSite / SoftwareApplication)は public/index.html に
// 静的に置いてあるので、ここでは重複させない。
//
// 出すのは次の2つ。
//   BlogPosting … 記事であること、公開日・更新日、書き手を伝える(Article の下位型)
//   FAQPage     … 記事内のQ&A。検索結果に質問と回答が展開されることを狙う
//
// 本文の文字列には '**強調**' などの独自記法が混ざっているため、
// 構造化データに載せる前に stripInline で必ず記号を落とす。
// 記号が残ったままだと、検索結果にそのまま '**' が出てしまう。

const PUBLISHER_ID = `${SITE_ORIGIN}/#organization`;

/**
 * 記事内の最初の画像を探す。
 * 記事固有の画像を構造化データに載せておくと、検索結果やDiscoverで
 * その記事の画像が使われる余地ができる(全記事が共通のOGP画像だとその余地がない)。
 */
const findFirstImage = (article: Article): string | null => {
    for (const section of article.sections) {
        for (const block of section.blocks) {
            if (block.type === 'image') {
                // ビルド後の src は '/static/media/...' の絶対パス。
                // 構造化データは相対パスを解決できないため、オリジンを足す
                return block.src.startsWith('http') ? block.src : `${SITE_ORIGIN}${block.src}`;
            }
        }
    }
    return null;
};

/** 記事そのものを説明する構造化データ */
export const buildArticleJsonLd = (article: Article) => {
    const url = `${SITE_ORIGIN}${articlePath(article.slug)}`;
    const firstImage = findFirstImage(article);

    return {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        '@id': `${url}#article`,
        mainEntityOfPage: { '@type': 'WebPage', '@id': url },
        headline: article.title,
        description: article.description,
        inLanguage: 'ja',
        datePublished: article.publishedAt,
        dateModified: article.updatedAt,
        // 記事固有の画像があればそれを、無ければサイト共通のOGP画像を使う
        image: firstImage ? [firstImage, `${SITE_ORIGIN}/ogp.png`] : `${SITE_ORIGIN}/ogp.png`,
        articleSection: article.category,
        // 書き手・発行元はいずれも運営会社。個人名を出す運用にしていないため法人を充てる
        author: { '@id': PUBLISHER_ID },
        publisher: { '@id': PUBLISHER_ID }
    };
};

/**
 * 記事内の faq ブロックから FAQPage を組み立てる。
 * Q&A が1件もない記事では null を返し、空の構造化データを出さないようにしている。
 */
export const buildFaqJsonLd = (article: Article) => {
    const items = article.sections
        .flatMap((section) => section.blocks)
        .filter((block): block is Extract<typeof block, { type: 'faq' }> => block.type === 'faq')
        .flatMap((block) => block.items);

    if (items.length === 0) {
        return null;
    }

    return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: items.map((item) => ({
            '@type': 'Question',
            name: stripInline(item.q),
            acceptedAnswer: { '@type': 'Answer', text: stripInline(item.a) }
        }))
    };
};

/**
 * 記事の一覧ページの構造化データ(CollectionPage)。
 * 全記事の一覧(/blog)と、カテゴリごとの一覧(/blog/category/<slug>)の両方で使う。
 *
 * 出している理由は2つある。
 *   1. 検索エンジンに「記事が集まっている場所」であることと、
 *      そこに何が載っているか(ItemList)を伝える。
 *      一覧ページ自体は本文を持たないため、これが無いと
 *      「記事へのリンクが並んでいるだけのページ」としか読めない
 *   2. sitemap.xml の lastmod をここから取る
 *      scripts/prerender.js は描画後のHTMLから dateModified を読んで lastmod に使う。
 *      記事ページは BlogPosting から取れるが、一覧ページには記事の構造化データが
 *      無いため、ここで dateModified を出しておかないと日付が取れない。
 *
 * dateModified はその一覧に載っている記事のうち、最も新しい updatedAt。
 * 記事を1本足せば、一覧の lastmod も自動で新しくなる。
 *
 * ★ slug を受け取らないのは、全記事の一覧(/blog)にはカテゴリの slug が無いため。
 *   URLは path で受け取るので、呼び出し側が何のページかを決められる。
 */
export const buildArticleListJsonLd = (
    page: { heading: string; description: string },
    articles: ArticleMeta[],
    path: string
) => ({
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: page.heading,
    description: page.description,
    url: `${SITE_ORIGIN}${path}`,
    dateModified: articles.reduce((latest, article) => (article.updatedAt > latest ? article.updatedAt : latest), articles[0].updatedAt),
    mainEntity: {
        '@type': 'ItemList',
        itemListElement: articles.map((article, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            url: `${SITE_ORIGIN}${articlePath(article.slug)}`,
            name: article.title
        }))
    }
});
